import {isValidEmail} from '~/lib/validation';
import React, {useState, useEffect, useRef, useCallback} from 'react';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';
import isEqual from 'lodash/isEqual';

import sy from '~/styles';
import {
  service_request as serviceRequestApi,
  dealer as dealerApi,
} from '~/api/private';
import {Dialog, FormTextInput, Text, Link} from '~/components/controls';
import {serviceRequestUpdate} from '~/actions';
import {useDispatch} from '~/lib/hooks';

import TextsmsIcon from '~/images/md-icons/textsms/materialicons/24px.svg';
import SmsFailedIcon from '~/images/md-icons/sms_failed/materialicons/24px.svg';
import EmailIcon from '~/images/md-icons/email/materialicons/24px.svg';
import PropTypes from 'prop-types';

const phoneRegex = /^06\d{8}$/i;
const getSmsErrorMessage = (smsNumber) => {
  if (!smsNumber?.length) {
    return 'Mobiele telefoonnummer is verplicht';
  }

  if (!phoneRegex.test(smsNumber)) {
    return 'Ongeldige waarde mobiele telefoonnummer';
  }

  return null;
};

const startStatusesPolling = ({poll, timerRef}) => {
  timerRef.current = setInterval(() => {
    poll().catch(() => {});
  }, 2000);
};

const StatusLink = ({reference_id, status, onSend}) => {
  switch (status) {
    case 'Delivered':
      return (
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>Verzonden</Text>
      );

    case 'SentFailed':
      return (
        <Text style={[sy.smallRegular, {color: '#972727'}]}>
          Verzenden mislukt
        </Text>
      );

    default:
      return (
        <Link
          disabled={!!reference_id}
          textStyle={[sy.smallRegular, sy['no-underline'], {color: '#2907E3'}]}
          onPress={onSend}>
          Verzenden
        </Link>
      );
  }
};
StatusLink.propTypes = {
  reference_id: PropTypes.any,
  status: PropTypes.any,
  onSend: PropTypes.any,
};

const EditSmsDialog = ({number, onDismiss, onChange}) => {
  const [smsNumber, setSmsNumber] = useState(number);
  const [phoneError, setPhoneError] = useState(false);

  return (
    <Dialog
      visible={true}
      title="SMS bericht"
      titleIcon={<TextsmsIcon fill="#4a4a49" />}
      onDismiss={onDismiss}
      buttons={[
        {
          text: 'Annuleren',
          onPress: onDismiss,
        },
        {
          text: 'OK',
          disabled: phoneError,
          onPress: () => {
            if (phoneError) {
              return;
            }

            onChange(smsNumber);
            onDismiss();
          },
        },
      ]}
      options={{
        noPaddingContent: true,
      }}>
      <View style={[sy['p-4'], sy['gap-4']]}>
        <div data-private>
          <FormTextInput
            value={smsNumber}
            onChangeText={setSmsNumber}
            onEndEditing={() => {
              const hasNumber = Boolean(smsNumber);
              const valid = !hasNumber || phoneRegex.test(smsNumber);
              setPhoneError(valid === false);
            }}
            label="Mobiele telefoonnummer"
            hasError={phoneError}
            errorMessage={getSmsErrorMessage(smsNumber)}
            inputProps={{
              maxLength: 10,
              keyboardType: 'numeric',
            }}
          />
        </div>
      </View>
    </Dialog>
  );
};
EditSmsDialog.propTypes = {
  number: PropTypes.any,
  onDismiss: PropTypes.any,
  onChange: PropTypes.any,
};

const EditEmailDialog = ({email, onDismiss, onChange}) => {
  const [emailAddress, setEmailAddress] = useState(email);
  const [emailError, setEmailError] = useState(false);

  return (
    <Dialog
      visible={true}
      title="Email bericht"
      titleIcon={<EmailIcon fill="#4a4a49" />}
      onDismiss={onDismiss}
      buttons={[
        {
          text: 'Annuleren',
          onPress: onDismiss,
        },
        {
          text: 'OK',
          disabled: emailError,
          onPress: () => {
            if (emailError) {
              return;
            }

            onChange(emailAddress);
            onDismiss();
          },
        },
      ]}
      options={{
        noPaddingContent: true,
      }}>
      <View style={[sy['p-4'], sy['gap-4']]}>
        <div data-private>
          <FormTextInput
            label="E-mail"
            value={emailAddress}
            onChangeText={setEmailAddress}
            onEndEditing={() => {
              const hasEmail = Boolean(emailAddress);
              const valid = !hasEmail || isValidEmail(emailAddress);
              setEmailError(valid === false);
            }}
            hasError={emailError}
            errorMessage="Email heeft een ongeldige waarde"
            inputProps={{
              keyboardType: 'email-address',
            }}
          />
        </div>
      </View>
    </Dialog>
  );
};
EditEmailDialog.propTypes = {
  email: PropTypes.any,
  onDismiss: PropTypes.any,
  onChange: PropTypes.any,
};

const DealerReminderDialog = ({
  visible,
  request_id,
  phone_number,
  email,
  onDismiss,
}) => {
  const dispatch = useDispatch();

  const [showEditSms, setShowEditSms] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [smsNumber, setSmsNumber] = useState(phone_number);
  const [emailAddress, setEmailAddress] = useState(email);
  const [references, setReferences] = useState(null);
  const [statuses, setStatuses] = useState(null);

  const timerID = useRef(null);
  const statusesRef = useRef(null);

  useEffect(() => {
    statusesRef.current = statuses;
  }, [statuses]);

  const pollStatuses = useCallback(async () => {
    const {result: nextStatuses} =
      await serviceRequestApi.client_message_statuses(request_id, references);

    if (!isEqual(statusesRef.current, nextStatuses)) {
      dispatch(
        serviceRequestUpdate({
          request_id,
        }),
      );
    }

    setStatuses(nextStatuses);
  }, [dispatch, references, request_id]);

  useEffect(() => {
    if (references) {
      if (timerID.current) {
        clearInterval(timerID.current);
      }

      startStatusesPolling({poll: pollStatuses, timerRef: timerID});
    }

    return () => {
      if (timerID.current) {
        clearInterval(timerID.current);
        setShowEditSms(false);
        setReferences(null);
        setStatuses(null);
      }
    };
  }, [pollStatuses, references]);

  if (showEditSms) {
    return (
      <EditSmsDialog
        number={smsNumber}
        onDismiss={() => setShowEditSms(false)}
        onChange={(number) => {
          setSmsNumber(number);
        }}
      />
    );
  }

  if (showEditEmail) {
    return (
      <EditEmailDialog
        email={emailAddress}
        onDismiss={() => setShowEditEmail(false)}
        onChange={setEmailAddress}
      />
    );
  }

  return (
    <Dialog
      visible={visible}
      title="Berichten naar berijder"
      onDismiss={onDismiss}
      buttons={[
        {
          text: 'Sluiten',
          onPress: onDismiss,
        },
      ]}
      options={{
        noPaddingContent: true,
      }}>
      <View>
        {smsNumber?.length > 0 && (
          <View style={[sy['p-4'], sy['flex-row'], sy['gap-8']]}>
            {statuses?.sms === 'SentFailed' ? (
              <SmsFailedIcon fill="#972727" />
            ) : (
              <TextsmsIcon fill="#4a4a49" />
            )}
            <View>
              <Text>SMS bericht</Text>
              <View style={sy['flex-row']}>
                <StatusLink
                  reference_id={references?.sms}
                  status={statuses?.sms}
                  onSend={async () => {
                    const {reference_id} = await dealerApi.reminder({
                      request_id,
                      phone_number: smsNumber,
                    });

                    setReferences({
                      ...references,
                      sms: reference_id,
                    });
                  }}
                />
                <Text>&nbsp;&middot;&nbsp;</Text>
                <Link
                  textStyle={[
                    sy.smallRegular,
                    sy['no-underline'],
                    {color: '#2907E3'},
                  ]}
                  onPress={() => setShowEditSms(true)}>
                  {references?.sms ? 'Bekijken' : 'Bewerken'}
                </Link>
              </View>
            </View>
          </View>
        )}
        {emailAddress?.length > 0 && (
          <>
            {smsNumber?.length > 0 && <Divider style={sy['bg-gray88']} />}
            <View style={[sy['p-4'], sy['flex-row'], sy['gap-8']]}>
              <EmailIcon fill="#4a4a49" />
              <View>
                <Text>Email bericht</Text>
                <View style={sy['flex-row']}>
                  <StatusLink
                    reference_id={references?.email}
                    status={references?.email ? 'Delivered' : null}
                    onSend={async () => {
                      const {reference_id} = await dealerApi.reminder({
                        request_id,
                        email: emailAddress,
                      });

                      setReferences({
                        ...references,
                        email: reference_id,
                      });
                    }}
                  />
                  <Text>&nbsp;&middot;&nbsp;</Text>
                  <Link
                    textStyle={[
                      sy.smallRegular,
                      sy['no-underline'],
                      {color: '#2907E3'},
                    ]}
                    onPress={() => setShowEditEmail(true)}>
                    {references?.email ? 'Bekijken' : 'Bewerken'}
                  </Link>
                </View>
              </View>
            </View>
          </>
        )}
      </View>
    </Dialog>
  );
};
DealerReminderDialog.propTypes = {
  visible: PropTypes.any,
  request_id: PropTypes.any,
  phone_number: PropTypes.any,
  email: PropTypes.any,
  onDismiss: PropTypes.any,
};

export default DealerReminderDialog;
